import httpservice from "./httpservice";
import fileDownloadService from "./fileDownloadService";
import moment from "moment";
import {
  MOCK_PROMO_CODES,
  MOCK_REDEMPTIONS,
} from "../modules/promo-codes/mock-data";

function cleanQuery(query = {}) {
  const cleanParams = {};
  Object.keys(query || {}).forEach((key) => {
    if (query[key] !== undefined && query[key] !== null && query[key] !== "") {
      cleanParams[key] = query[key];
    }
  });
  return cleanParams;
}

function normalizePaginated(res, defaultLimit = 10, defaultPage = 1) {
  if (!res?.data) return null;

  const envelope = res.data;
  const payload = envelope.data !== undefined ? envelope.data : envelope;

  let rawList = [];
  let meta = {};

  if (Array.isArray(payload)) {
    rawList = payload;
    meta = envelope;
  } else if (payload && typeof payload === "object") {
    meta = payload;
    if (Array.isArray(payload.data)) {
      rawList = payload.data;
    } else if (Array.isArray(payload.docs)) {
      rawList = payload.docs;
    } else if (Array.isArray(payload.promoCodes)) {
      rawList = payload.promoCodes;
    } else if (Array.isArray(payload.list)) {
      rawList = payload.list;
    } else if (Array.isArray(payload.records)) {
      rawList = payload.records;
    } else if (Array.isArray(envelope.docs)) {
      rawList = envelope.docs;
    } else if (Array.isArray(envelope.data)) {
      rawList = envelope.data;
    } else if (Array.isArray(envelope.promoCodes)) {
      rawList = envelope.promoCodes;
    }
  }

  // Ensure each doc has an `id` property (map from `_id` if missing)
  const docs = rawList.map((item) => {
    if (!item) return item;
    if (!item.id && item._id) {
      return { ...item, id: String(item._id) };
    }
    return item;
  });

  const totalDocs = Number(meta.totalDocs ?? meta.total ?? meta.count ?? docs.length) || 0;
  const limit = Number(meta.limit ?? defaultLimit) || 10;
  const page = Number(meta.page ?? defaultPage) || 1;
  const totalPages = Number(meta.totalPages ?? meta.pages ?? Math.ceil(totalDocs / limit)) || 1;
  const hasNextPage = meta.hasNextPage !== undefined ? Boolean(meta.hasNextPage) : page < totalPages;
  const hasPrevPage = meta.hasPrevPage !== undefined ? Boolean(meta.hasPrevPage) : page > 1;

  return {
    docs,
    totalDocs,
    limit,
    page,
    totalPages,
    hasNextPage,
    hasPrevPage,
  };
}

class PromoCodeService {
  constructor() {
    this.endpoints = {
      stats: "/admin/cp/marketing/promo-codes/stats",
      analytics: "/admin/cp/marketing/promo-codes/analytics",
      promoCodes: "/admin/cp/marketing/promo-codes",
      single: "/admin/cp/marketing/promo-codes/single",
      generate: "/admin/cp/marketing/promo-codes/generate",
      status: (id) => `/admin/cp/marketing/promo-codes/${id}/status`,
      update: (id) => `/admin/cp/marketing/promo-codes/${id}`,
      delete: (id) => `/admin/cp/marketing/promo-codes/${id}`,
      redemptions: "/admin/cp/marketing/promo-codes/redemptions",
      exportPromoCodes: "/admin/cp/marketing/promo-codes/export",
      exportRedemptions: "/admin/cp/marketing/promo-codes/redemptions/export",
    };
    // Internal state for mock mutation fallback
    this._mockPromoCodes = [...MOCK_PROMO_CODES];
    this._mockRedemptions = [...MOCK_REDEMPTIONS];
  }

  // 1. Dashboard KPIs
  async getOverviewStats(query = {}) {
    try {
      const res = await httpservice.get(this.endpoints.stats, { params: query });
      if (res?.data?.data) return res.data.data;
      if (res?.data && typeof res.data === "object") return res.data;
    } catch (e) {
      // quiet fallback to zeros on network error
    }
    return {
      codesGenerated: 0,
      activeCodes: 0,
      redemptions: 0,
      discountGiven: 0,
      revenueGenerated: 0,
    };
  }

  // 2. Performance Analytics
  async getAnalytics(query = {}) {
    const params = {};
    if (query.range) params.range = query.range;
    if (query.datePreset) params.datePreset = query.datePreset;
    if (query.dateFrom) params.dateFrom = query.dateFrom;
    if (query.dateTo) params.dateTo = query.dateTo;

    try {
      const res = await httpservice.get(this.endpoints.analytics, { params });
      if (res?.data?.data) return res.data.data;
      if (res?.data && typeof res.data === "object") return res.data;
    } catch (e) {
      // quiet fallback on network error
    }
    return {
      attributedGrossRevenue: 0,
      totalRedemptions: 0,
      totalDiscount: 0,
      series: [],
    };
  }

  // 3. List Promo Codes
  async getPromoCodes(query = {}) {
    const params = {};
    if (query.page) params.page = query.page;
    if (query.limit) params.limit = query.limit;
    if (query.keyword && query.keyword.trim()) params.keyword = query.keyword.trim();
    if (query.status && query.status.toUpperCase() !== "ALL") {
      params.status = query.status.toUpperCase();
    }
    if (query.plan && query.plan.toUpperCase() !== "ALL") {
      params.plan = query.plan;
    }
    if (query.discountType && query.discountType.toUpperCase() !== "ALL") {
      params.discountType = query.discountType.toUpperCase();
    }
    if (query.dateFrom) params.dateFrom = query.dateFrom;
    if (query.dateTo) params.dateTo = query.dateTo;

    try {
      const res = await httpservice.get(this.endpoints.promoCodes, { params });
      if (res?.data) {
        const normalized = normalizePaginated(res, query.limit || 10, query.page || 1);
        if (normalized) return normalized;
      }
    } catch (e) {
      // quiet fallback
    }

    let items = [...this._mockPromoCodes];
    if (query?.keyword) {
      const kw = query.keyword.toLowerCase().trim();
      items = items.filter(
        (item) =>
          (item.code || "").toLowerCase().includes(kw) ||
          (item.description || "").toLowerCase().includes(kw) ||
          (item.discount || "").toLowerCase().includes(kw) ||
          (item.createdBy?.name || "").toLowerCase().includes(kw)
      );
    }
    if (query?.status && query.status.toUpperCase() !== "ALL") {
      items = items.filter(
        (item) => (item.status || "").toLowerCase() === query.status.toLowerCase()
      );
    }
    if (query?.plan && query.plan.toUpperCase() !== "ALL") {
      items = items.filter(
        (item) =>
          item.planSelect === "ALL" ||
          (item.applicablePlans || []).includes("All Plans") ||
          (item.applicablePlans || []).some(
            (p) => p.toLowerCase() === query.plan.toLowerCase()
          )
      );
    }

    const page = Number(query.page) || 1;
    const limit = Number(query.limit) || 10;
    const totalDocs = items.length;
    const totalPages = Math.ceil(totalDocs / limit) || 1;
    const paginatedDocs = items.slice((page - 1) * limit, page * limit);

    return {
      docs: paginatedDocs,
      totalDocs: totalDocs,
      limit: limit,
      page: page,
      totalPages: totalPages,
    };
  }

  // 4. Get Single Promo Code
  async getPromoCode(id) {
    try {
      const res = await httpservice.get(this.endpoints.single, { params: { id } });
      if (res?.data?.data) return res.data.data;
    } catch (e) {
      // fallback
    }
    return this._mockPromoCodes.find((item) => item.id === id) || null;
  }

  // 5. Create Promo Code
  async createPromoCode(payload) {
    try {
      const res = await httpservice.post(this.endpoints.promoCodes, payload);
      if (res?.data) return res.data;
    } catch (e) {
      if (e?.response?.data) {
        throw e;
      }
    }

    // Add to mock store as fallback
    const newCode = {
      id: `pc-${Date.now()}`,
      code: payload.code || "PROMO" + Math.floor(1000 + Math.random() * 9000),
      description: payload.description || "",
      discount:
        payload.discountType === "PERCENTAGE"
          ? `${payload.amount}% off`
          : payload.discountType === "FIXED"
          ? `₦${Number(payload.amount).toLocaleString()} off`
          : "Special Offer",
      discountType: payload.discountType || "PERCENTAGE",
      amount: payload.amount || 20,
      amountType: payload.amountType || "PERCENTAGE",
      maxDiscountAmount: payload.maxDiscountAmount || 100000,
      planSelect: payload.planSelect || "ALL",
      applicablePlans: payload.plans?.length ? payload.plans : ["All Plans"],
      usage: 0,
      limit: payload.usage?.totalLimit || 100,
      validity: payload.schedule?.neverExpires
        ? "Ongoing"
        : `${payload.schedule?.startAt ? moment(payload.schedule.startAt).format("DD-MM") : ""} → ${payload.schedule?.expiryAt ? moment(payload.schedule.expiryAt).format("DD-MM") : ""}`,
      startDate: payload.schedule?.startAt || new Date().toISOString(),
      endDate: payload.schedule?.expiryAt || "",
      isExpired: false,
      status: "ACTIVE",
      isActive: true,
      revenue: 0,
      createdBy: {
        name: "Admin",
        initials: "AD",
        bgColor: "#F1F5F9",
        textColor: "#475569",
      },
    };
    this._mockPromoCodes.unshift(newCode);
    return { status: "SUCCESS", data: newCode };
  }

  // 6. Auto-generate code
  async generateCode(prefix = "JUREB") {
    try {
      const res = await httpservice.get(this.endpoints.generate, {
        params: { prefix },
      });
      if (res?.data?.data?.code) return res.data.data.code;
    } catch (e) {
      // fallback
    }
    const randomSuffix = Math.random().toString(36).substring(2, 8).toUpperCase();
    return `${prefix}${randomSuffix}`;
  }

  // 7. Update Promo Code
  async updatePromoCode(id, payload) {
    try {
      const patchFn = httpservice.patch || (httpservice.axios && httpservice.axios.patch.bind(httpservice.axios));
      const res = patchFn
        ? await patchFn(this.endpoints.update(id), payload)
        : await httpservice.axios.request({
            method: "patch",
            url: this.endpoints.update(id),
            data: payload,
          });
      if (res?.data) return res.data;
    } catch (e) {
      if (e?.response?.data) throw e;
    }
    const idx = this._mockPromoCodes.findIndex((c) => c.id === id);
    if (idx !== -1) {
      this._mockPromoCodes[idx] = { ...this._mockPromoCodes[idx], ...payload };
    }
    return { status: "SUCCESS" };
  }

  // 8. Pause / Reactivate
  async updatePromoCodeStatus(id, isActive) {
    try {
      const patchFn = httpservice.patch || (httpservice.axios && httpservice.axios.patch.bind(httpservice.axios));
      const res = patchFn
        ? await patchFn(this.endpoints.status(id), { isActive })
        : await httpservice.axios.request({
            method: "patch",
            url: this.endpoints.status(id),
            data: { isActive },
          });
      if (res?.data) return res.data;
    } catch (e) {
      if (e?.response?.data) throw e;
    }
    const idx = this._mockPromoCodes.findIndex((c) => c.id === id);
    if (idx !== -1) {
      this._mockPromoCodes[idx].isActive = isActive;
      this._mockPromoCodes[idx].status = isActive ? "ACTIVE" : "PAUSED";
    }
    return { status: "SUCCESS" };
  }

  // 9. Delete Promo Code
  async deletePromoCode(id) {
    try {
      const res = await httpservice.delete(this.endpoints.delete(id));
      if (res?.data) return res.data;
    } catch (e) {
      if (e?.response?.data) throw e;
    }
    this._mockPromoCodes = this._mockPromoCodes.filter((c) => c.id !== id);
    return { status: "SUCCESS" };
  }

  // 10. Redemption History
  async getRedemptions(query = {}) {
    const params = {};
    if (query.page) params.page = query.page;
    if (query.limit) params.limit = query.limit;
    if (query.keyword && query.keyword.trim()) params.keyword = query.keyword.trim();
    if (query.code && query.code.trim()) params.code = query.code.trim();
    if (query.status && query.status.toUpperCase() !== "ALL") {
      params.status = query.status.toUpperCase();
    }
    if (query.platform && query.platform.toUpperCase() !== "ALL") {
      params.platform = query.platform.toUpperCase();
    }
    if (query.plan && query.plan.toUpperCase() !== "ALL") {
      params.plan = query.plan;
    }
    if (query.dateFrom) params.dateFrom = query.dateFrom;
    if (query.dateTo) params.dateTo = query.dateTo;

    try {
      const res = await httpservice.get(this.endpoints.redemptions, { params });
      if (res?.data) {
        const normalized = normalizePaginated(res, query.limit || 10, query.page || 1);
        if (normalized) return normalized;
      }
    } catch (e) {
      // fallback to mock store
    }

    let items = [...this._mockRedemptions];
    if (query?.keyword) {
      const kw = query.keyword.toLowerCase().trim();
      items = items.filter(
        (item) =>
          (item.customerName || item.customer?.name || "").toLowerCase().includes(kw) ||
          (item.companyName || item.customer?.company || "").toLowerCase().includes(kw) ||
          (item.email || item.customer?.email || "").toLowerCase().includes(kw) ||
          (item.promoCode || item.code || "").toLowerCase().includes(kw)
      );
    }
    if (query?.plan && query.plan.toUpperCase() !== "ALL") {
      const pl = query.plan.toLowerCase();
      items = items.filter(
        (item) => (item.planTier || item.productName || "").toLowerCase().includes(pl)
      );
    }
    if (query?.platform && query.platform.toUpperCase() !== "ALL") {
      items = items.filter(
        (item) => (item.platform || "").toLowerCase() === query.platform.toLowerCase()
      );
    }
    if (query?.status && query.status.toUpperCase() !== "ALL") {
      items = items.filter(
        (item) => (item.status || "").toLowerCase() === query.status.toLowerCase()
      );
    }

    const page = Number(query.page) || 1;
    const limit = Number(query.limit) || 10;
    const totalDocs = items.length;
    const totalPages = Math.ceil(totalDocs / limit) || 1;
    const paginatedDocs = items.slice((page - 1) * limit, page * limit);

    return {
      docs: paginatedDocs,
      totalDocs: totalDocs,
      limit: limit,
      page: page,
      totalPages: totalPages,
    };
  }

  // 11. CSV Export for Promo Codes (direct backend file stream)
  async exportPromoCodes(query = {}) {
    const cleanParams = cleanQuery(query);
    const fileName = `promo_codes_${moment().format("YYYYMMDD_HHmmss")}.csv`;
    return await fileDownloadService.download(
      this.endpoints.exportPromoCodes,
      fileName,
      null,
      cleanParams
    );
  }

  // 12. CSV Export for Redemptions (direct backend file stream)
  async exportRedemptions(query = {}) {
    const cleanParams = cleanQuery(query);
    const fileName = `promo_redemptions_${moment().format("YYYYMMDD_HHmmss")}.csv`;
    return await fileDownloadService.download(
      this.endpoints.exportRedemptions,
      fileName,
      null,
      cleanParams
    );
  }
}

const promoCodeService = new PromoCodeService();
export default promoCodeService;
